import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMediaQuery } from '@aimsos/hooks';
import Skeleton from '../Skeleton.jsx';
import SidebarItem from './SidebarItem.jsx';
import SidebarToggle from './SidebarToggle.jsx';
import { resolveSidebarVariant } from './sidebarVariants.js';
import { useSidebarSubmenuDismiss } from './useSidebarSubmenuDismiss.js';

/** @typedef {import('./sidebarTypes.js').SidebarNavItem} SidebarNavItem */
/** @typedef {import('./sidebarTypes.js').SidebarSubmenuNavItem} SidebarSubmenuNavItem */

const SidebarContext = createContext(null);

export const useSidebarContext = () => {
  const ctx = useContext(SidebarContext);
  if (ctx === null) throw new Error('useSidebarContext must be used inside <Sidebar>');
  return ctx;
};

const panelBase = 'flex min-h-0 flex-1 flex-col p-2';
const panelFloatingClass = 'border-[0.5px] border-[#bababa]/60';
const SIDEBAR_WIDTH_MIN_PX = 40;
const SIDEBAR_WIDTH_MAX_PX = 400;
const EMPTY_ITEMS = [];

/**
 * Sidebar — primary navigation (Figma: Sidebar Component Compress / Expanded).
 *
 * @param {object} props
 * @param {SidebarNavItem[]} [props.items=[]]
 * @param {React.ReactNode} [props.header]
 * @param {React.ReactNode} [props.footer]
 * @param {boolean} [props.expanded=false]
 * @param {boolean} [props.showToggle=true]
 * @param {Function} [props.onToggleExpand]
 * @param {string} [props.activeItem]
 * @param {Function} [props.onItemClick]
 * @param {'default' | 'primary' | 'frost'} [props.variant='default']
 * @param {boolean} [props.submenuOpen=false]
 * @param {Function} [props.onSubmenuOpenChange]
 * @param {SidebarSubmenuNavItem[]} [props.submenuItems=[]]
 * @param {string} [props.submenuActiveItem]
 * @param {(id: string, item: SidebarSubmenuNavItem) => void} [props.onSubmenuItemClick]
 * @param {boolean} [props.floating=true]
 * @param {number} [props.collapsedWidth]
 * @param {number} [props.mobileCollapsedWidth]
 * @param {number} [props.expandedWidth]
 * @param {boolean} [props.loading=false] - Shows animated skeleton placeholders while nav items load.
 * @param {number} [props.skeletonCount=5] - Number of skeleton rows to show when `loading` is true.
 * @param {string} [props.className]
 */
const Sidebar = ({
  items = EMPTY_ITEMS,
  header,
  footer,
  expanded = false,
  showToggle = true,
  onToggleExpand,
  activeItem,
  onItemClick,
  variant = 'default',
  submenuOpen = false,
  onSubmenuOpenChange,
  submenuItems = EMPTY_ITEMS,
  submenuActiveItem,
  onSubmenuItemClick,
  floating = true,
  collapsedWidth,
  mobileCollapsedWidth,
  expandedWidth,
  loading = false,
  skeletonCount = 5,
  className = '',
}) => {
  const sidebarRef = useRef(null);
  const submenuPanelRef = useRef(null);
  const itemRefs = useRef(new Map());
  const hoverOpenTimerRef = useRef(null);
  const hoverCloseTimerRef = useRef(null);
  const [submenuParentItemId, setSubmenuParentItemId] = useState(null);
  const [submenuPos, setSubmenuPos] = useState(null);
  const palette = useMemo(() => resolveSidebarVariant(variant), [variant]);
  const isMobile = useMediaQuery('(max-width: 767px)');

  // Roving tabindex — tracks which nav item is the current "rover" (tabIndex=0)
  const [focusedItemIndex, setFocusedItemIndex] = useState(() => {
    const idx = items.findIndex((i) => i.id === activeItem);
    return idx >= 0 ? idx : 0;
  });

  // Keep rover in sync when activeItem changes externally
  useEffect(() => {
    const idx = items.findIndex((i) => i.id === activeItem);
    if (idx >= 0) setFocusedItemIndex(idx);
  }, [activeItem, items]);

  const padding = floating ? 16 : 0;
  const asideWidthPx = useMemo(() => {
    if (expanded) {
      return expandedWidth != null ? expandedWidth + padding : 250;
    }
    if (isMobile) {
      const w = mobileCollapsedWidth ?? (floating ? 54 : 54);
      return w + padding;
    }
    const w = collapsedWidth ?? (floating ? 80 : 64);
    return w + padding;
  }, [expanded, expandedWidth, collapsedWidth, mobileCollapsedWidth, floating, padding, isMobile]);

  const normalizedAsideWidthPx = useMemo(
    () => Math.max(SIDEBAR_WIDTH_MIN_PX, Math.min(SIDEBAR_WIDTH_MAX_PX, Math.round(asideWidthPx))),
    [asideWidthPx],
  );
  const asideWidthClass = useMemo(() => `w-[${normalizedAsideWidthPx}px]`, [normalizedAsideWidthPx]);
  const itemsById = useMemo(() => new Map(items.map((item) => [item.id, item])), [items]);

  const resolveSubmenuEntriesForItem = useCallback(
    (itemId) => {
      const item = itemsById.get(itemId);
      return Array.isArray(item?.items) ? item.items : [];
    },
    [itemsById],
  );

  // Ref keeps onSubmenuOpenChange fresh inside stable callbacks without adding it to deps
  const onSubmenuOpenChangeRef = useRef(onSubmenuOpenChange);
  onSubmenuOpenChangeRef.current = onSubmenuOpenChange;

  const closeSubmenu = useCallback(() => {
    setSubmenuParentItemId(null);
    onSubmenuOpenChangeRef.current?.(false);
  }, []);

  const openSubmenuForItem = useCallback(
    (itemId) => {
      const entries = resolveSubmenuEntriesForItem(itemId);
      if (!entries.length) return;
      setSubmenuParentItemId(itemId);
      onSubmenuOpenChangeRef.current?.(true);
    },
    [resolveSubmenuEntriesForItem],
  );

  // Hover open: 150ms delay so accidental cursor passes don't trigger.
  // No isMobile guard needed: touch devices don't fire mouseenter, and the
  // flyout render is already gated by !(isMobile && expanded).
  const handleItemMouseEnter = useCallback(
    (itemId) => {
      if (!resolveSubmenuEntriesForItem(itemId).length) return;
      clearTimeout(hoverCloseTimerRef.current);
      hoverOpenTimerRef.current = setTimeout(() => openSubmenuForItem(itemId), 150);
    },
    [resolveSubmenuEntriesForItem, openSubmenuForItem],
  );

  // Hover leave from a nav item: start close timer (gives time to reach flyout)
  const handleItemMouseLeave = useCallback(() => {
    clearTimeout(hoverOpenTimerRef.current);
    hoverCloseTimerRef.current = setTimeout(closeSubmenu, 200);
  }, [closeSubmenu]);

  // Entering the flyout cancels the close timer
  const handleFlyoutMouseEnter = useCallback(() => {
    clearTimeout(hoverCloseTimerRef.current);
  }, []);

  // Leaving the flyout restarts the close timer
  const handleFlyoutMouseLeave = useCallback(() => {
    hoverCloseTimerRef.current = setTimeout(closeSubmenu, 200);
  }, [closeSubmenu]);

  // Clean up hover timers on unmount
  useEffect(
    () => () => {
      clearTimeout(hoverOpenTimerRef.current);
      clearTimeout(hoverCloseTimerRef.current);
    },
    [],
  );

  // H6: prevent itemRefs Map from holding stale DOM nodes
  useEffect(() => () => itemRefs.current.clear(), []);
  useEffect(() => {
    const ids = new Set(items.map((i) => i.id));
    itemRefs.current.forEach((_, id) => {
      if (!ids.has(id)) itemRefs.current.delete(id);
    });
  }, [items]);

  const handleToggleExpand = useCallback(() => {
    if (submenuOpen) closeSubmenu();
    onToggleExpand?.();
  }, [submenuOpen, closeSubmenu, onToggleExpand]);

  const ctx = useMemo(
    () => ({
      expanded,
      activeItem,
      setActiveItem: onItemClick,
      variant,
      submenuOpen,
      closeSubmenu,
      sidebarRef,
    }),
    [expanded, activeItem, onItemClick, variant, submenuOpen, closeSubmenu],
  );

  const toggle = showToggle ? (
    <SidebarToggle expanded={expanded} onClick={handleToggleExpand} />
  ) : null;

  const toggleSubmenuForItem = useCallback(
    (itemId) => {
      const entries = resolveSubmenuEntriesForItem(itemId);
      if (entries.length === 0) {
        closeSubmenu();
        return;
      }
      const isAlreadyOpen = submenuOpen && submenuParentItemId === itemId;
      if (isAlreadyOpen) {
        closeSubmenu();
        return;
      }
      setSubmenuParentItemId(itemId);
      onSubmenuOpenChange?.(true);
    },
    [
      closeSubmenu,
      onSubmenuOpenChange,
      resolveSubmenuEntriesForItem,
      submenuOpen,
      submenuParentItemId,
    ],
  );

  useEffect(() => {
    if (!submenuParentItemId) return;
    const parent = itemsById.get(submenuParentItemId);
    if (!parent) {
      setSubmenuParentItemId(null);
      return;
    }
    const entries = Array.isArray(parent.items) ? parent.items : [];
    if (entries.length === 0) {
      setSubmenuParentItemId(null);
    }
  }, [itemsById, submenuParentItemId]);

  useEffect(() => {
    if (!submenuOpen || !submenuParentItemId) {
      setSubmenuPos(null);
      return;
    }
    const updatePos = () => {
      const itemEl = itemRefs.current.get(submenuParentItemId);
      const sidebarEl = sidebarRef.current;
      if (!itemEl || !sidebarEl) return;
      const itemRect = itemEl.getBoundingClientRect();
      const sidebarRect = sidebarEl.getBoundingClientRect();
      setSubmenuPos({ top: itemRect.top, left: sidebarRect.right + 4 });
    };
    updatePos();
    window.addEventListener('scroll', updatePos, { passive: true, capture: true });
    window.addEventListener('resize', updatePos, { passive: true });
    return () => {
      window.removeEventListener('scroll', updatePos, { capture: true });
      window.removeEventListener('resize', updatePos);
    };
  }, [submenuOpen, submenuParentItemId]);

  // Auto-focus the first flyout item when it opens (desktop only)
  useEffect(() => {
    if (submenuOpen && !(isMobile && expanded) && submenuPanelRef.current) {
      requestAnimationFrame(() => {
        submenuPanelRef.current?.querySelector('button')?.focus();
      });
    }
  }, [submenuOpen, isMobile, expanded]);

  const activeSubmenuItems = useMemo(() => {
    if (submenuParentItemId) {
      const entries = resolveSubmenuEntriesForItem(submenuParentItemId);
      if (entries.length > 0) return entries;
    }
    if (activeItem) {
      const entries = resolveSubmenuEntriesForItem(activeItem);
      if (entries.length > 0) return entries;
    }
    return submenuItems;
  }, [submenuParentItemId, activeItem, resolveSubmenuEntriesForItem, submenuItems]);

  const hasSubmenuItems = activeSubmenuItems.length > 0;

  useSidebarSubmenuDismiss({
    open: submenuOpen && hasSubmenuItems,
    onClose: closeSubmenu,
    sidebarRef,
    panelRef: submenuPanelRef,
  });

  // Arrow key navigation within the <nav> (roving tabindex pattern)
  const handleNavKeyDown = useCallback(
    (event) => {
      // Let the flyout handle its own keys when open
      if (submenuOpen) return;
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
      // Only handle when a nav item button has focus
      if (!document.activeElement?.matches('[data-nav-item]')) return;

      const navButtons = [
        ...(sidebarRef.current?.querySelectorAll('[data-nav-item]') ?? []),
      ];
      if (!navButtons.length) return;

      const curr = navButtons.indexOf(document.activeElement);
      event.preventDefault();

      if (event.key === 'ArrowDown') {
        const next = curr < navButtons.length - 1 ? curr + 1 : 0;
        navButtons[next].focus();
      } else if (event.key === 'ArrowUp') {
        const prev = curr > 0 ? curr - 1 : navButtons.length - 1;
        navButtons[prev].focus();
      } else if (event.key === 'Home') {
        navButtons[0].focus();
      } else if (event.key === 'End') {
        navButtons[navButtons.length - 1].focus();
      }
    },
    [submenuOpen],
  );

  // Focus trap + Esc for the desktop flyout panel
  const handleFlyoutKeyDown = useCallback(
    (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        const parentId = submenuParentItemId;
        closeSubmenu();
        // Return focus to the trigger item after React re-renders
        requestAnimationFrame(() => {
          sidebarRef.current?.querySelector(`[data-nav-item="${parentId}"]`)?.focus();
        });
        return;
      }

      if (event.key === 'Tab') {
        const focusable = [
          ...(submenuPanelRef.current?.querySelectorAll('button') ?? []),
        ];
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    },
    [closeSubmenu, submenuParentItemId],
  );

  const skeletonEntries = useMemo(() => {
    if (!loading) return null;
    return Array.from({ length: skeletonCount }, (_, i) => (
      <div
        key={i}
        className={
          expanded
            ? 'flex min-h-8 w-full items-center gap-3 pr-1'
            : 'flex w-full justify-center py-0.5'
        }
        aria-hidden
      >
        <Skeleton
          width={32}
          height={32}
          borderRadius={8}
          animationStyle="shimmer"
          className={palette.skeletonClass}
        />
        {expanded && (
          <Skeleton
            height={12}
            width={`${45 + (i * 23) % 40}%`}
            borderRadius={4}
            animationStyle="shimmer"
            className={palette.skeletonClass}
          />
        )}
      </div>
    ));
  }, [loading, skeletonCount, expanded, palette.skeletonClass]);

  const navEntries = useMemo(
    () =>
      items.map((item, index) => (
        <SidebarItem
          key={item.id}
          itemId={item.id}
          icon={item.icon}
          label={item.label}
          hasSubmenu={Boolean(item.hasSubmenu) || resolveSubmenuEntriesForItem(item.id).length > 0}
          badge={item.badge}
          submenuOpen={submenuOpen && submenuParentItemId === item.id}
          onSubmenuToggle={() => toggleSubmenuForItem(item.id)}
          tabIndex={index === focusedItemIndex ? 0 : -1}
          onFocus={() => setFocusedItemIndex(index)}
          onClick={() => {
            if (resolveSubmenuEntriesForItem(item.id).length === 0 && submenuOpen) {
              closeSubmenu();
            }
            item.onClick?.();
          }}
        />
      )),
    [
      closeSubmenu,
      focusedItemIndex,
      items,
      resolveSubmenuEntriesForItem,
      submenuOpen,
      submenuParentItemId,
      toggleSubmenuForItem,
    ],
  );

  const navRows = useMemo(
    () =>
      navEntries.map((entry, mapIndex) => {
        const itemId = items[mapIndex]?.id;
        const refCallback = (el) => {
          if (!itemId) return;
          if (el) itemRefs.current.set(itemId, el);
          else itemRefs.current.delete(itemId);
        };

        const isAccordionOpen =
          isMobile && expanded && submenuOpen && submenuParentItemId === itemId;
        const accordionEntries = isAccordionOpen
          ? resolveSubmenuEntriesForItem(itemId)
          : [];

        const accordionBlock =
          accordionEntries.length > 0 ? (
            <div
              className={[
                'grid transition-[grid-template-rows] duration-200 ease-in-out',
                isAccordionOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
              ].join(' ')}
            >
              <div className="overflow-hidden">
                <div
                  role="menu"
                  aria-label={itemsById.get(itemId)?.label}
                  className="flex flex-col py-2 pl-4"
                >
                  {accordionEntries.map((subItem) => {
                    const isActive = submenuActiveItem === subItem.id;
                    return (
                      <button
                        key={subItem.id}
                        type="button"
                        role="menuitem"
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') {
                            e.preventDefault();
                            closeSubmenu();
                            requestAnimationFrame(() => {
                              sidebarRef.current
                                ?.querySelector(`[data-nav-item="${itemId}"]`)
                                ?.focus();
                            });
                          }
                        }}
                        onClick={() => {
                          onSubmenuItemClick?.(subItem.id, subItem);
                          subItem.onClick?.();
                        }}
                        className={[
                          'w-full rounded-lg px-3 py-2 text-left text-xs font-medium leading-5 transition-colors duration-150',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset',
                          palette.submenuFocusRing,
                          isActive
                            ? palette.submenuActive
                            : [palette.accordionTextInactive, palette.rowHoverBg].join(' '),
                        ].join(' ')}
                      >
                        {subItem.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : null;

        const hasSubmenuEntries = resolveSubmenuEntriesForItem(itemId).length > 0;
        const hoverProps = hasSubmenuEntries
          ? {
              onMouseEnter: () => handleItemMouseEnter(itemId),
              onMouseLeave: handleItemMouseLeave,
            }
          : {};

        return (
          <div
            key={itemId ?? `sidebar-item-${mapIndex}`}
            ref={refCallback}
            className="w-full shrink-0"
            {...hoverProps}
          >
            {entry}
            {accordionBlock}
          </div>
        );
      }),
    [
      closeSubmenu,
      expanded,
      handleItemMouseEnter,
      handleItemMouseLeave,
      isMobile,
      items,
      itemsById,
      navEntries,
      onSubmenuItemClick,
      palette,
      resolveSubmenuEntriesForItem,
      submenuActiveItem,
      submenuOpen,
      submenuParentItemId,
    ],
  );

  return (
    <SidebarContext.Provider value={ctx}>
      <>
        <aside
          ref={sidebarRef}
          className={[
            'flex h-full shrink-0 flex-col transition-[width] duration-200 ease-in-out',
            asideWidthClass,
            floating ? 'p-2' : '',
            className,
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <div
            className={[
              panelBase,
              palette.panelBg,
              floating && palette.panelShadow,
              floating && panelFloatingClass,
              expanded ? 'items-start' : 'items-center',
              floating && (expanded ? 'rounded-2xl' : 'rounded-lg'),
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {header && expanded && (
              <div className="mb-4 w-full shrink-0 overflow-hidden">{header}</div>
            )}

            <nav
              id="sidebar-main-nav"
              aria-label="Main navigation"
              aria-busy={loading || undefined}
              onKeyDown={handleNavKeyDown}
              className={`flex w-full min-h-0 flex-1 flex-col gap-4 overflow-y-auto scrollbar-thin scrollbar-track-transparent ${palette.scrollbarThumb}`}
            >
              {toggle ? (
                <div
                  className={[
                    'flex h-8 w-full shrink-0 items-center',
                    expanded ? 'justify-end' : 'justify-center',
                  ].join(' ')}
                >
                  {toggle}
                </div>
              ) : null}

              {loading ? skeletonEntries : navRows}
            </nav>

            {footer && expanded && (
              <div className={`mt-4 w-full shrink-0 border-t pt-2 ${palette.footerBorder}`}>
                {footer}
              </div>
            )}
          </div>
        </aside>

        {submenuOpen && hasSubmenuItems && submenuPos && !(isMobile && expanded) ? (
          <div
            ref={submenuPanelRef}
            className="fixed z-50"
            style={{ top: submenuPos.top, left: submenuPos.left }}
            onKeyDown={handleFlyoutKeyDown}
            onMouseEnter={handleFlyoutMouseEnter}
            onMouseLeave={handleFlyoutMouseLeave}
          >
            <div
              role="menu"
              aria-label={itemsById.get(submenuParentItemId)?.label}
              className={`flex min-w-[260px] flex-col overflow-hidden rounded-lg ${palette.submenuBg} ${palette.submenuShadow}`}
            >
              {activeSubmenuItems.map((item) => {
                const isActive = submenuActiveItem === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      onSubmenuItemClick?.(item.id, item);
                      item.onClick?.();
                      closeSubmenu();
                    }}
                    className={[
                      'w-full px-4 py-2 text-left text-sm font-medium leading-5 transition-colors duration-150',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset',
                      palette.submenuFocusRing,
                      isActive
                        ? palette.submenuActive
                        : [palette.submenuTextInactive, palette.rowHoverBg].join(' '),
                    ].join(' ')}
                  >
                    {item.label}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
      </>
    </SidebarContext.Provider>
  );
};

export default Sidebar;
